/**
 * Parent Data Analysis Script
 *
 * Analyzes all parent users to understand:
 * - Telegram ID presence
 * - Linked children count and structure
 * - Data quality issues
 *
 * Usage: await window.analyzeParentData()
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const TARGET_SCHOOL_ID = 'hktnikYSv6l6vGPbjMmG';

/**
 * Check if parent has Telegram set up via their linked children
 * Telegram data is stored in parentChildLinks, not user documents
 */
function analyzeTelegram(links) {
  if (!links || links.length === 0) {
    return {
      hasTelegram: false,
      verifiedCount: 0,
      totalLinks: 0,
      chatIds: [],
      usernames: []
    };
  }

  const verifiedLinks = links.filter(link =>
    link.telegram?.verified && link.telegram?.chatId
  );

  return {
    hasTelegram: verifiedLinks.length > 0,
    verifiedCount: verifiedLinks.length,
    totalLinks: links.length,
    chatIds: verifiedLinks.map(l => l.telegram.chatId),
    usernames: verifiedLinks.map(l => l.telegram.username).filter(Boolean)
  };
}

/**
 * Get linked children for a parent user
 * Note: Field is 'parentId' not 'parentUid' in parentChildLinks collection
 */
async function getLinkedChildren(parentId) {
  try {
    const linksRef = collection(db, 'parentChildLinks');
    const q = query(linksRef, where('parentId', '==', parentId));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error getting children for parent ${parentId}:`, error);
    return [];
  }
}

/**
 * Main analysis function
 */
export async function analyzeParentData() {
  try {
    console.log('\n' + '='.repeat(80));
    console.log('PARENT DATA ANALYSIS');
    console.log('='.repeat(80));
    console.log(`Target School: ${TARGET_SCHOOL_ID}\n`);

    // 1. Query all parent users
    console.log('📊 Querying parent users...');
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('parent', '==', true),
      where('schoolId', '==', TARGET_SCHOOL_ID)
    );
    const snapshot = await getDocs(q);

    const parents = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log(`Found ${parents.length} parent users\n`);

    // 2. Analyze each parent
    console.log('🔍 Analyzing parent data...\n');

    const analysis = {
      total: parents.length,
      withTelegram: 0,
      withoutTelegram: 0,
      withChildren: 0,
      withoutChildren: 0,
      telegramFields: {},
      childrenCounts: {},
      issues: [],
      parents: []
    };

    for (const parent of parents) {
      const links = await getLinkedChildren(parent.id);
      const telegram = analyzeTelegram(links);

      // Count stats
      if (telegram.hasTelegram) {
        analysis.withTelegram++;
        // Track how many links have Telegram verified
        const key = `${telegram.verifiedCount}/${telegram.totalLinks}`;
        analysis.telegramFields[key] = (analysis.telegramFields[key] || 0) + 1;
      } else {
        analysis.withoutTelegram++;
      }

      if (links.length > 0) {
        analysis.withChildren++;
        analysis.childrenCounts[links.length] = (analysis.childrenCounts[links.length] || 0) + 1;
      } else {
        analysis.withoutChildren++;
      }

      // Track issues
      const parentIssues = [];
      if (!telegram.hasTelegram) parentIssues.push('NO_TELEGRAM');
      if (links.length === 0) parentIssues.push('NO_CHILDREN');
      if (!parent.email) parentIssues.push('NO_EMAIL');
      if (!parent.displayName && !parent.firstName) parentIssues.push('NO_NAME');

      if (parentIssues.length > 0) {
        analysis.issues.push({
          uid: parent.id,
          email: parent.email,
          name: parent.displayName || `${parent.firstName || ''} ${parent.lastName || ''}`.trim(),
          issues: parentIssues,
          childrenCount: links.length,
          telegram: telegram.hasTelegram,
          telegramVerified: telegram.verifiedCount,
          telegramTotal: telegram.totalLinks
        });
      }

      analysis.parents.push({
        uid: parent.id,
        email: parent.email,
        name: parent.displayName || `${parent.firstName || ''} ${parent.lastName || ''}`.trim(),
        hasTelegram: telegram.hasTelegram,
        telegramVerified: telegram.verifiedCount,
        telegramTotal: telegram.totalLinks,
        telegramChatIds: telegram.chatIds,
        telegramUsernames: telegram.usernames,
        childrenCount: links.length,
        children: links.map(c => ({
          linkId: c.id,
          studentId: c.studentId,
          studentName: c.studentName,
          relationship: c.relationship,
          telegramVerified: c.telegram?.verified || false,
          telegramChatId: c.telegram?.chatId || null,
          telegramUsername: c.telegram?.username || null
        })),
        createdAt: parent.createdAt,
        schoolId: parent.schoolId,
        schoolIds: parent.schoolIds,
        staff: parent.staff,
        admin: parent.admin
      });
    }

    // 3. Print summary report
    console.log('=' .repeat(80));
    console.log('📈 SUMMARY REPORT');
    console.log('='.repeat(80));
    console.log(`\nTotal Parents: ${analysis.total}`);
    console.log(`\n📱 Telegram Status:`);
    console.log(`   ✅ With Telegram: ${analysis.withTelegram} (${Math.round(analysis.withTelegram/analysis.total*100)}%)`);
    console.log(`   ❌ Without Telegram: ${analysis.withoutTelegram} (${Math.round(analysis.withoutTelegram/analysis.total*100)}%)`);

    if (Object.keys(analysis.telegramFields).length > 0) {
      console.log(`\n   Telegram Verification Distribution (verified/total links):`);
      for (const [field, count] of Object.entries(analysis.telegramFields)) {
        console.log(`      ${field} links verified: ${count} parents`);
      }
    }

    console.log(`\n👨‍👩‍👧‍👦 Children Linking Status:`);
    console.log(`   ✅ With Children: ${analysis.withChildren} (${Math.round(analysis.withChildren/analysis.total*100)}%)`);
    console.log(`   ❌ Without Children: ${analysis.withoutChildren} (${Math.round(analysis.withoutChildren/analysis.total*100)}%)`);

    if (Object.keys(analysis.childrenCounts).length > 0) {
      console.log(`\n   Children Count Distribution:`);
      const sortedCounts = Object.entries(analysis.childrenCounts).sort(([a], [b]) => parseInt(a) - parseInt(b));
      for (const [count, parents] of sortedCounts) {
        console.log(`      ${count} child${count > 1 ? 'ren' : ''}: ${parents} parents`);
      }
    }

    // 4. Data quality issues
    if (analysis.issues.length > 0) {
      console.log(`\n⚠️  DATA QUALITY ISSUES (${analysis.issues.length} parents):`);
      console.log('='.repeat(80));

      const grouped = {
        NO_TELEGRAM: [],
        NO_CHILDREN: [],
        NO_EMAIL: [],
        NO_NAME: []
      };

      for (const issue of analysis.issues) {
        for (const issueType of issue.issues) {
          grouped[issueType].push(issue);
        }
      }

      if (grouped.NO_TELEGRAM.length > 0) {
        console.log(`\n❌ Parents WITHOUT Telegram (${grouped.NO_TELEGRAM.length}):`);
        grouped.NO_TELEGRAM.slice(0, 10).forEach(p => {
          const telegramInfo = p.telegramTotal > 0
            ? `${p.telegramVerified}/${p.telegramTotal} links verified`
            : 'no links';
          console.log(`   • ${p.name || 'No Name'} (${p.email}) - ${p.childrenCount} children (${telegramInfo})`);
        });
        if (grouped.NO_TELEGRAM.length > 10) {
          console.log(`   ... and ${grouped.NO_TELEGRAM.length - 10} more`);
        }
      }

      if (grouped.NO_CHILDREN.length > 0) {
        console.log(`\n❌ Parents WITHOUT Children (${grouped.NO_CHILDREN.length}):`);
        grouped.NO_CHILDREN.slice(0, 10).forEach(p => {
          console.log(`   • ${p.name || 'No Name'} (${p.email}) - Telegram: ${p.telegram ? 'Yes' : 'No'}`);
        });
        if (grouped.NO_CHILDREN.length > 10) {
          console.log(`   ... and ${grouped.NO_CHILDREN.length - 10} more`);
        }
      }

      if (grouped.NO_EMAIL.length > 0) {
        console.log(`\n❌ Parents WITHOUT Email (${grouped.NO_EMAIL.length}):`);
        grouped.NO_EMAIL.forEach(p => {
          console.log(`   • UID: ${p.uid}`);
        });
      }

      if (grouped.NO_NAME.length > 0) {
        console.log(`\n❌ Parents WITHOUT Name (${grouped.NO_NAME.length}):`);
        grouped.NO_NAME.slice(0, 10).forEach(p => {
          console.log(`   • ${p.email}`);
        });
        if (grouped.NO_NAME.length > 10) {
          console.log(`   ... and ${grouped.NO_NAME.length - 10} more`);
        }
      }
    } else {
      console.log(`\n✅ NO DATA QUALITY ISSUES FOUND!`);
    }

    // 5. Check for duplicate children
    console.log(`\n🔍 DUPLICATE CHILDREN CHECK:`);
    console.log('='.repeat(80));

    const studentIdMap = new Map();
    for (const parent of analysis.parents) {
      for (const child of parent.children) {
        if (!studentIdMap.has(child.studentId)) {
          studentIdMap.set(child.studentId, []);
        }
        studentIdMap.get(child.studentId).push({
          parentUid: parent.uid,
          parentName: parent.name,
          parentEmail: parent.email,
          relationship: child.relationship
        });
      }
    }

    const duplicates = Array.from(studentIdMap.entries())
      .filter(([_, parents]) => parents.length > 1);

    if (duplicates.length > 0) {
      console.log(`\n⚠️  Found ${duplicates.length} students with multiple parent links:\n`);
      duplicates.forEach(([studentId, parents]) => {
        console.log(`   Student ID: ${studentId}`);
        console.log(`   Student Name: ${parents[0].relationship ? 'N/A' : 'N/A'}`);
        console.log(`   Linked to ${parents.length} parents:`);
        parents.forEach(p => {
          console.log(`      • ${p.parentName} (${p.parentEmail}) - ${p.relationship || 'No relationship'}`);
        });
        console.log('');
      });
    } else {
      console.log(`\n✅ No duplicate children found - each student linked to only one parent`);
    }

    console.log('\n' + '='.repeat(80));
    console.log('✅ ANALYSIS COMPLETE');
    console.log('='.repeat(80));
    console.log('\n💾 Full data stored in: window._parentAnalysis');
    console.log('   Access with: window._parentAnalysis.parents\n');

    // Store full analysis in window for further inspection
    window._parentAnalysis = analysis;

    return analysis;

  } catch (error) {
    console.error('❌ Analysis failed:', error);
    throw error;
  }
}
